import { Active, Over } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ColumnOrderState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { usePageContext, useResetFinalSchema } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  companyIdSelector,
  fixedColumnNamesAF,
  IColumnDef,
  IExtendableDto,
  Log,
  tableColumnsOrderStateAF,
  tableColumnsVisibilityAF,
  unique,
  updateFinalSchemaLocalStore,
  VisibilityState,
} from "@ryde/shared";

import { TableSettingsDrawerInner } from "./table-settings.drawer.inner";
import { ITableSettingsDrawerState } from "./table-settings.models";
import {
  getActiveItems,
  getInactiveItems,
  isOutOfSort,
} from "./table-settings.utils";

export interface ITableSettingsDrawerProps<T> {
  tableDefs: IColumnDef<object, any>[];
}

export const TableSettingsDrawer = <T extends IExtendableDto>({
  tableDefs,
}: ITableSettingsDrawerProps<T>): JSX.Element => {
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);

  const [columnOrder, setColumnOrder] = useRecoilState(
    tableColumnsOrderStateAF(entity),
  );

  const [columnVisibility, setColumnVisibility] = useRecoilState(
    tableColumnsVisibilityAF(entity),
  );
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));
  const fixedColumnNames = useRecoilValue(fixedColumnNamesAF(entity));
  const { resetFinalSchema } = useResetFinalSchema();

  const items: IColumnDef<object, any>[] = useMemo(
    () => tableDefs?.filter(x => !isOutOfSort(x)) || [],
    [tableDefs],
  );

  const [state, setState] = useState<ITableSettingsDrawerState<object>>({
    activeColumns: getActiveItems(items, columnVisibility, columnOrder),
    inactiveColumns: getInactiveItems(items, columnVisibility),
  });

  useEffect(() => {
    setState({
      activeColumns: getActiveItems(items, columnVisibility, columnOrder),
      inactiveColumns: getInactiveItems(items, columnVisibility),
    });
  }, [columnOrder, columnVisibility, items]);

  const getComputedColumnOrder = (
    ordered: ColumnOrderState,
  ): ColumnOrderState => {
    const updatedColumns = unique([
      ...fixedColumnNames.fixedColumns,
      ...ordered,
      ...fixedColumnNames.lastColumns,
    ]);
    Log.debug("Table settings drawer, set column orders to ", updatedColumns);
    return updatedColumns;
  };

  const onDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }): void => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = state.activeColumns.findIndex(
      item => item.id === active.id,
    );
    const newIndex = state.activeColumns.findIndex(item => item.id === over.id);

    const newActiveColumns = arrayMove(state.activeColumns, oldIndex, newIndex);
    const ordered = newActiveColumns.map(el => el.id);

    if (ordered.length && setColumnOrder) {
      const computedColumnOrder = getComputedColumnOrder(
        ordered as ColumnOrderState,
      );
      setColumnOrder(computedColumnOrder);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsOrder: computedColumnOrder },
        });
      }

      setActiveViewId(undefined);
    }

    setState(prevState => ({
      ...prevState,
      activeColumns: newActiveColumns,
    }));
  };

  const onVisibilityChange = useEvent(
    (newSwitchState: "on" | "off", dragItemId: string | undefined): void => {
      if (!dragItemId) {
        Log.warn("Invalid drag item");
        return;
      }

      let columnsRemoveFrom =
        newSwitchState === "off" ? state.activeColumns : state.inactiveColumns;
      const columnsAddTo =
        newSwitchState === "off" ? state.inactiveColumns : state.activeColumns;

      const dragItem = columnsRemoveFrom.find(x => x.id === dragItemId);
      columnsRemoveFrom = columnsRemoveFrom.filter(x => x.id !== dragItemId);

      if (dragItem) {
        columnsAddTo.push(dragItem);
      }

      const newActiveColumns =
        newSwitchState === "off" ? columnsRemoveFrom : columnsAddTo;
      const newInactiveColumns =
        newSwitchState === "off" ? columnsAddTo : columnsRemoveFrom;

      setState({
        activeColumns: [...newActiveColumns],
        inactiveColumns: [...newInactiveColumns],
      });

      const updated: VisibilityState = {};
      updated[dragItemId] = newSwitchState === "on";

      if (setColumnVisibility) {
        setColumnVisibility(colVisibility => ({
          ...colVisibility,
          ...updated,
        }));

        if (companyId) {
          updateFinalSchemaLocalStore({
            companyId,
            page: entity,
            finalSchema: {
              columnsVisibility: {
                ...columnVisibility,
                ...updated,
              },
            },
          });
        }

        setActiveViewId(undefined);
      }

      if (setColumnOrder) {
        const computedColumns = [
          ...newActiveColumns
            .map(column => column.id)
            .filter(x => x !== undefined),
        ];
        const updatedColumnOrder = getComputedColumnOrder(computedColumns);
        setColumnOrder(updatedColumnOrder);

        if (companyId) {
          updateFinalSchemaLocalStore({
            companyId,
            page: entity,
            finalSchema: { columnsOrder: updatedColumnOrder },
          });
        }

        setActiveViewId(undefined);
      }
    },
  );

  return (
    <TableSettingsDrawerInner
      state={state}
      onDragEnd={onDragEnd}
      onVisibilityChange={onVisibilityChange}
      onReset={resetFinalSchema}
    />
  );
};
